import { createContext, useContext, useEffect, useState } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';

type LicenseType = 'pro' | 'hire';
type UserMode = 'standard' | 'pro' | 'hire' | 'admin';

interface AuthContextProps {
  session: Session | null;
  user: User | null;
  signUp: (email: string, password: string, fullName: string) => Promise<{ error: any }>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  loading: boolean;
  currentMode: UserMode;
  activeLicenses: LicenseType[];
  setMode: (mode: UserMode) => Promise<void>;
  hasLicense: (type: LicenseType) => boolean;
  isCompanyAdmin: boolean;
  hasRecruiterLicense: boolean;
  toggleRecruiterMode: () => Promise<void>;
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentMode, setCurrentMode] = useState<UserMode>('standard');
  const [activeLicenses, setActiveLicenses] = useState<LicenseType[]>([]);
  const [isCompanyAdmin, setIsCompanyAdmin] = useState(false);
  const [hasRecruiterLicense, setHasRecruiterLicense] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    // Prevent double initialization
    if (initialized) {
      return;
    }
    
    setInitialized(true);

    const setData = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) {
          console.error('Error getting session:', error);
        }
        setSession(session);
        setUser(session?.user ?? null);
        
        if (session?.user) {
          await fetchUserProfile(session.user.id); // Ensure fetchUserProfile completes
        } else {
          setLoading(false);
          setCurrentMode('standard');
          setActiveLicenses([]);
          setIsCompanyAdmin(false);
          setHasRecruiterLicense(false);
        }
      } catch (err) {
        console.error('Error in setData:', err);
        setLoading(false); // Ensure loading is set to false on error
      }
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        await fetchUserProfile(session.user.id); // Ensure fetchUserProfile completes
      } else {
        setLoading(false);
        setCurrentMode('standard');
        setActiveLicenses([]);
        setIsCompanyAdmin(false);
        setHasRecruiterLicense(false);
      }
    });

    setData();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const fetchUserProfile = async (userId: string) => {
    // Ensure loading is true before fetching
    if (!loading) setLoading(true); 
    try {
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (profileError) throw profileError;

      if (profile) {
        setCurrentMode(profile.current_mode as UserMode);
        setActiveLicenses(profile.active_licenses || []);
        setHasRecruiterLicense((profile.active_licenses || []).includes('hire'));
        setIsCompanyAdmin(!!profile.primary_company_id);
      } else {
         // Reset to defaults if profile is null/not found
         setCurrentMode('standard');
         setActiveLicenses([]);
         setHasRecruiterLicense(false);
         setIsCompanyAdmin(false);
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
      // Reset state on error
      setCurrentMode('standard');
      setActiveLicenses([]);
      setHasRecruiterLicense(false);
      setIsCompanyAdmin(false);
    } finally {
      setLoading(false);
    }
  };

  const signUp = async (email: string, password: string, fullName: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: fullName,
        },
      },
    });
    return { error };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setCurrentMode('standard');
    setActiveLicenses([]);
    setIsCompanyAdmin(false);
    setHasRecruiterLicense(false);
  };

  const setMode = async (newMode: UserMode) => {
    if (!user) return;
    
    try {
      const { error } = await supabase.rpc('set_user_mode', {
        p_user_id: user.id,
        p_new_mode: newMode
      });

      if (error) throw error;
      
      setCurrentMode(newMode);
      
      // Only show toast when manually switching modes
      if (newMode !== currentMode) {
        toast({
          title: `NewLoop ${newMode.charAt(0).toUpperCase() + newMode.slice(1)}`,
          description: newMode === 'admin' ? 'Admin mode enabled' : 
                      newMode === 'hire' ? 'Hiring features enabled' : 
                      newMode === 'pro' ? 'Pro features enabled' : 
                      'Using standard features',
        });
      }
      
      // Navigate based on new mode
      if (newMode === 'admin') {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
      
    } catch (error: any) {
      console.error('Error updating mode:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.message || 'Failed to switch modes. Please try again.',
      });
      throw error;
    }
  };

  const hasLicense = (type: LicenseType) => activeLicenses.includes(type);

  const toggleRecruiterMode = async () => {
    if (!user) return;
    
    try {
      // If user has pro license and is in standard mode, switch to pro first
      if (activeLicenses.includes('pro') && currentMode === 'standard') {
        await setMode('pro');
        return;
      }
      
      // If user is in pro mode and has hire license, switch to hire
      if (currentMode === 'pro' && activeLicenses.includes('hire')) {
        await setMode('hire');
        return;
      }
      
      // If user is in hire mode, switch back to standard
      if (currentMode === 'hire') {
        await setMode('standard');
        return;
      }
      
      // If user has hire license but is in standard mode, switch to hire
      if (activeLicenses.includes('hire') && currentMode === 'standard') {
        await setMode('hire');
        return;
      }
      
      // If no special conditions met, toggle between current mode and standard
      const newMode = currentMode === 'standard' ? 
        (activeLicenses.includes('hire') ? 'hire' : 'pro') : 
        'standard';
      await setMode(newMode);
      
    } catch (error) {
      console.error('Error toggling mode:', error);
      throw error;
    }
  };

  const value = {
    session,
    user,
    signUp,
    signIn,
    signOut,
    loading,
    currentMode,
    activeLicenses,
    setMode,
    hasLicense,
    isCompanyAdmin,
    hasRecruiterLicense,
    toggleRecruiterMode,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}